import React, { useState, useEffect } from "react";
import { ref, onValue, update, remove } from "firebase/database";
import { database } from "../firebase/firebaseConfig";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const ReviewPage = () => {
  const [reviews, setReviews] = useState([]);
  const [editingReview, setEditingReview] = useState(null); // Track the review being edited
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch reviews from Firebase Realtime Database
    const reviewsRef = ref(database, "testimonials/");
    onValue(reviewsRef, (snapshot) => {
      const reviewsData = snapshot.val();
      const reviewsList = reviewsData
        ? Object.keys(reviewsData).map((key) => ({
            id: key,
            ...reviewsData[key],
          }))
        : [];
      setReviews(reviewsList);
    });
  }, []);

  const handleEdit = (review) => {
    setEditingReview(review); // Set the review to be edited
  };

  const handleDelete = (reviewId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        // Delete review from Firebase
        const reviewRef = ref(database, `testimonials/${reviewId}`);
        remove(reviewRef)
          .then(() => {
            Swal.fire("Deleted!", "The review has been deleted.", "success");
          })
          .catch((error) => {
            Swal.fire("Error", "There was an issue deleting the review.", "error");
          });
      }
    });
  };

  const handleUpdate = () => {
    if (editingReview) {
      // Update review in Firebase
      const reviewRef = ref(database, `testimonials/${editingReview.id}`);
      update(reviewRef, {
        name: editingReview.name,
        rating: editingReview.rating,
        testimonial: editingReview.testimonial,
      }).then(() => {
        Swal.fire("Updated!", "The review has been updated.", "success");
        setEditingReview(null); // Clear the editing state
      }).catch((error) => {
        Swal.fire("Error", "There was an issue updating the review.", "error");
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditingReview((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <button
        onClick={() => navigate(-1)} // Navigate back to the previous page
        className="mb-4 bg-blue-500 text-white py-2 px-4 rounded-lg shadow-md hover:bg-blue-600"
      >
        Back
      </button>
      <h1 className="text-3xl font-bold text-center mb-6">Customer Reviews</h1>
      <div className="overflow-x-auto bg-white rounded-lg shadow-md">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="py-3 px-4 border-b text-left">Name</th>
              <th className="py-3 px-4 border-b text-left">Star Rating</th>
              <th className="py-3 px-4 border-b text-left">Review</th>
              <th className="py-3 px-4 border-b text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {reviews.map((review) => (
              <tr key={review.id} className="hover:bg-gray-100">
                <td className="py-3 px-4 border-b">
                  {editingReview && editingReview.id === review.id ? (
                    <input
                      type="text"
                      name="name"
                      value={editingReview.name}
                      onChange={handleChange}
                      className="border rounded px-2 py-1"
                    />
                  ) : (
                    review.name
                  )}
                </td>
                <td className="py-3 px-4 border-b">
                  {editingReview && editingReview.id === review.id ? (
                    <input
                      type="number"
                      name="rating"
                      value={editingReview.rating}
                      onChange={handleChange}
                      className="border rounded px-2 py-1"
                      min="1"
                      max="5"
                    />
                  ) : (
                    "⭐".repeat(review.rating)
                  )}
                </td>
                <td className="py-3 px-4 border-b">
                  {editingReview && editingReview.id === review.id ? (
                    <textarea
                      name="testimonial"
                      value={editingReview.testimonial}
                      onChange={handleChange}
                      className="border rounded px-2 py-1"
                    />
                  ) : (
                    review.testimonial
                  )}
                </td>
                <td className="py-3 px-4 border-b">
                  {editingReview && editingReview.id === review.id ? (
                    <button
                      onClick={handleUpdate}
                      className="bg-green-500 text-white py-1 px-4 rounded-lg"
                    >
                      Update
                    </button>
                  ) : (
                    <button
                      onClick={() => handleEdit(review)}
                      className="bg-yellow-500 text-white py-1 px-4 rounded-lg mr-2"
                    >
                      Edit
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(review.id)}
                    className="bg-red-500 text-white py-1 px-4 rounded-lg"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReviewPage;
